import { api } from './client';
import type {
  ClientWithPortal,
  CreateClientRequest,
  UpdateClientRequest,
  UpdatePortalRequest,
  PortalFile,
  PortalActivity,
  PublicPortalInfo,
  PublicPortalFile,
  PublicPortalProject,
  PublicPortalFolder,
} from '@affogato/shared';

// Response types
interface ClientsListResponse {
  clients: ClientWithPortal[];
}

interface ClientResponse {
  client: ClientWithPortal;
}

interface ClientDetailResponse {
  client: ClientWithPortal & {
    portalFiles: PortalFile[];
    activities: PortalActivity[];
  };
}

interface PortalResponse {
  portal: {
    id: string;
    clientId: string;
    token: string;
    hasPassword: boolean;
    isActive: boolean;
    brandingConfig: { logo?: string; primaryColor?: string; companyName?: string } | null;
    createdAt: string;
    updatedAt: string;
  };
}

interface PortalFilesResponse {
  files: PortalFile[];
  added: number;
  skipped: number;
}

interface RegenerateTokenResponse {
  token: string;
}

// Public portal types
interface PublicPortalInfoResponse {
  portal: PublicPortalInfo;
}

interface PublicPortalFilesResponse {
  files: PublicPortalFile[];
}

interface PublicDownloadResponse {
  downloadUrl: string;
  fileName: string;
}

interface PublicPortalProjectsResponse {
  projects: PublicPortalProject[];
}

interface PublicPortalFoldersResponse {
  folders: PublicPortalFolder[];
}

// Clients API
export const clientsApi = {
  list: () => api.get<ClientsListResponse>('/clients'),

  get: (id: string) => api.get<ClientDetailResponse>(`/clients/${id}`),

  create: (data: CreateClientRequest) =>
    api.post<ClientResponse>('/clients', data),

  update: (id: string, data: UpdateClientRequest) =>
    api.patch<ClientResponse>(`/clients/${id}`, data),

  delete: (id: string) => api.delete<{ success: boolean }>(`/clients/${id}`),
};

// Portals API
export const portalsApi = {
  update: (clientId: string, data: UpdatePortalRequest) =>
    api.patch<PortalResponse>(`/portals/${clientId}`, data),

  regenerateToken: (clientId: string) =>
    api.post<RegenerateTokenResponse>(`/portals/${clientId}/regenerate-token`),

  addFiles: (clientId: string, fileIds: string[]) =>
    api.post<PortalFilesResponse>(`/portals/${clientId}/files`, { fileIds }),

  removeFile: (clientId: string, portalFileId: string) =>
    api.delete<{ success: boolean }>(`/portals/${clientId}/files/${portalFileId}`),

  reorderFiles: (clientId: string, fileOrders: { portalFileId: string; order: number }[]) =>
    api.patch<{ success: boolean }>(`/portals/${clientId}/files/reorder`, { fileOrders }),
};

// Public Portal API (for clients accessing the portal)
export const publicPortalApi = {
  getInfo: (token: string) =>
    api.get<PublicPortalInfoResponse>(`/p/${token}`),

  verifyPassword: async (token: string, password: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || '/api'}/p/${token}/verify`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Verification failed' }));
      throw { status: response.status, message: error.message };
    }

    return response.json() as Promise<{ verified: boolean }>;
  },

  getFiles: async (token: string, password?: string) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (password) {
      headers['X-Portal-Password'] = password;
    }

    const response = await fetch(
      `${import.meta.env.VITE_API_URL || '/api'}/p/${token}/files`,
      { method: 'GET', headers }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to get files' }));
      throw { status: response.status, message: error.message };
    }

    return response.json() as Promise<PublicPortalFilesResponse>;
  },

  downloadFile: async (token: string, fileId: string, password?: string) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (password) {
      headers['X-Portal-Password'] = password;
    }

    const response = await fetch(
      `${import.meta.env.VITE_API_URL || '/api'}/p/${token}/download/${fileId}`,
      { method: 'GET', headers }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Download failed' }));
      throw { status: response.status, message: error.message };
    }

    return response.json() as Promise<PublicDownloadResponse>;
  },

  getReport: async (token: string, password?: string) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (password) {
      headers['X-Portal-Password'] = password;
    }

    const response = await fetch(
      `${import.meta.env.VITE_API_URL || '/api'}/p/${token}/report`,
      { method: 'GET', headers }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to get report' }));
      throw { status: response.status, message: error.message };
    }

    return response.json() as Promise<{
      report: {
        id: string;
        title: string;
        content: string;
        publishedAt: string;
      } | null;
    }>;
  },

  // Project-based portal access
  getProjects: async (token: string, password?: string) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (password) {
      headers['X-Portal-Password'] = password;
    }

    const response = await fetch(
      `${import.meta.env.VITE_API_URL || '/api'}/p/${token}/projects`,
      { method: 'GET', headers }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to get projects' }));
      throw { status: response.status, message: error.message };
    }

    return response.json() as Promise<PublicPortalProjectsResponse>;
  },

  getProjectFolders: async (token: string, projectId: string, parentId?: string | null, password?: string) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (password) {
      headers['X-Portal-Password'] = password;
    }

    const params = parentId ? `?parentId=${parentId}` : '';
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || '/api'}/p/${token}/projects/${projectId}/folders${params}`,
      { method: 'GET', headers }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to get folders' }));
      throw { status: response.status, message: error.message };
    }

    return response.json() as Promise<PublicPortalFoldersResponse>;
  },

  getProjectFiles: async (token: string, projectId: string, folderId: string, password?: string) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (password) {
      headers['X-Portal-Password'] = password;
    }

    const response = await fetch(
      `${import.meta.env.VITE_API_URL || '/api'}/p/${token}/projects/${projectId}/files?folderId=${folderId}`,
      { method: 'GET', headers }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to get files' }));
      throw { status: response.status, message: error.message };
    }

    return response.json() as Promise<PublicPortalFilesResponse>;
  },

  downloadProjectFile: async (token: string, projectId: string, fileId: string, password?: string) => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (password) {
      headers['X-Portal-Password'] = password;
    }

    const response = await fetch(
      `${import.meta.env.VITE_API_URL || '/api'}/p/${token}/projects/${projectId}/download/${fileId}`,
      { method: 'GET', headers }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Download failed' }));
      throw { status: response.status, message: error.message };
    }

    return response.json() as Promise<PublicDownloadResponse>;
  },
};
